import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { workersAPI } from '../api/api';
import { useWorkerLang } from '../contexts/WorkerLanguageContext';
import AvailabilityBadge from '../components/AvailabilityBadge';
import VerifiedBadge from '../components/VerifiedBadge';
import FastResponderBadge from '../components/FastResponderBadge';
import LanguagesPicker, { LANGUAGE_CATALOG } from '../components/LanguagesPicker';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { 
  Star, 
  MapPin, 
  MagnifyingGlass, 
  Funnel, 
  CaretDown, 
  CaretUp,
  X,
  CheckCircle,
  User,
  SpinnerGap,
  Briefcase,
  ChatCircle,
  ShieldCheck,
  SealCheck
} from '@phosphor-icons/react';

const FindWorkers = () => {
  const navigate = useNavigate();
  const { lang } = useWorkerLang();
  const L = (en, es) => (lang === 'es' ? es : en);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  
  const [kind, setKind] = useState('workers');
  const [filters, setFilters] = useState({
    search: '',
    trade: 'all',
    location: '',
    minRating: 0,
    availability: '',
    experience: '',
    verifiedOnly: false,
    languages: [],
    sortBy: 'rating'
  });
  
  const [activeFilters, setActiveFilters] = useState([]);

  const tradeOptions = [
    { value: 'all', label: L('All Trades', 'Todos los Oficios') },
    { value: 'electrical', label: L('Electrical', 'Electricidad') },
    { value: 'plumbing', label: L('Plumbing', 'Plomería') },
    { value: 'carpentry', label: L('Carpentry', 'Carpintería') },
    { value: 'hvac', label: L('HVAC', 'Climatización') },
    { value: 'roofing', label: L('Roofing', 'Techado') },
    { value: 'painting', label: L('Painting', 'Pintura') },
    { value: 'masonry', label: L('Masonry', 'Albañilería') },
    { value: 'drywall', label: L('Drywall', 'Tablaroca') },
    { value: 'flooring', label: L('Flooring', 'Pisos') },
    { value: 'welding', label: L('Welding', 'Soldadura') }
  ];

  const fetchWorkers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 12,
        sort_by: filters.sortBy,
        kind
      };
      
      if (filters.search) params.search = filters.search;
      if (filters.trade !== 'all') params.trade = filters.trade;
      if (filters.location) params.location = filters.location;
      if (filters.minRating > 0) params.min_rating = filters.minRating;
      if (filters.availability) params.availability = filters.availability;
      if (filters.experience) params.experience = filters.experience;
      if (filters.verifiedOnly) params.verified_only = true;
      if (filters.languages && filters.languages.length > 0) params.languages = filters.languages.join(',');
      
      const response = await workersAPI.getAll(params);
      setWorkers(response.data.workers);
      setTotal(response.data.total);
      
      const active = [];
      if (filters.search) active.push({ key: 'search', label: `${L('Search', 'Búsqueda')}: ${filters.search}` });
      if (filters.trade !== 'all') active.push({ key: 'trade', label: `${L('Trade', 'Oficio')}: ${filters.trade}` });
      if (filters.location) active.push({ key: 'location', label: `${L('Location', 'Ubicación')}: ${filters.location}` });
      if (filters.minRating > 0) active.push({ key: 'minRating', label: `${L('Min Rating', 'Calificación Mín.')}: ${filters.minRating}★` });
      if (filters.availability) active.push({ key: 'availability', label: `${L('Availability', 'Disponibilidad')}: ${filters.availability}` });
      if (filters.experience) active.push({ key: 'experience', label: `${L('Experience', 'Experiencia')}: ${filters.experience}` });
      if (filters.verifiedOnly) active.push({ key: 'verifiedOnly', label: L('Verified Only', 'Solo Verificados') });
      if (filters.languages && filters.languages.length > 0) {
        active.push({ key: 'languages', label: `${L('Speaks', 'Habla')}: ${filters.languages.join(', ').toUpperCase()}` });
      }
      setActiveFilters(active);
    } catch (error) {
      console.error('Error fetching workers:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filters, lang, kind]);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  const clearFilter = (key) => {
    if (key === 'trade') {
      setFilters(prev => ({ ...prev, trade: 'all' }));
    } else if (key === 'minRating') {
      setFilters(prev => ({ ...prev, minRating: 0 }));
    } else if (key === 'verifiedOnly') {
      setFilters(prev => ({ ...prev, verifiedOnly: false }));
    } else if (key === 'languages') {
      setFilters(prev => ({ ...prev, languages: [] }));
    } else {
      setFilters(prev => ({ ...prev, [key]: '' }));
    }
  };

  const clearAllFilters = () => {
    setFilters({
      search: '',
      trade: 'all',
      location: '',
      minRating: 0,
      availability: '',
      experience: '',
      verifiedOnly: false,
      languages: [],
      sortBy: 'rating'
    });
  };

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 pb-24 transition-colors duration-300" data-testid="find-workers-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black dark:border-[#2B4C6F]">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-black">
            {kind === 'workers' ? L('Find Workers', 'Buscar Trabajadores') : L('Find Subcontractors', 'Buscar Subcontratistas')}
          </h1>
          <p className="text-blue-100 text-lg mt-1">
            {total} {kind === 'workers'
              ? L('skilled workers available', 'trabajadores calificados disponibles')
              : L('licensed subcontractors available', 'subcontratistas con licencia disponibles')}
          </p>
          <div className="inline-flex mt-4 rounded-xl overflow-hidden border-2 border-black bg-white" data-testid="kind-toggle">
            {[
              { value: 'workers', label: L('Workers', 'Trabajadores') },
              { value: 'subcontractors', label: L('Subcontractors', 'Subcontratistas') },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => { setKind(opt.value); }}
                className={`px-5 py-2 font-bold text-sm transition-colors ${
                  kind === opt.value ? 'bg-[#EA580C] text-white' : 'bg-white text-neutral-700 hover:bg-neutral-100'
                }`}
                data-testid={`kind-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Search Bar */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-4 mb-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlass size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input
                placeholder={L('Search by name or skill...', 'Buscar por nombre o habilidad...')}
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl dark:bg-neutral-700 dark:text-white"
                data-testid="search-input"
              />
            </div>
            <div className="relative">
              <MapPin size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input
                placeholder={L('Location', 'Ubicación')}
                value={filters.location}
                onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl w-full md:w-48 dark:bg-neutral-700 dark:text-white"
                data-testid="location-input"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`h-14 px-6 flex items-center gap-2 font-bold text-lg rounded-xl border-2 border-black dark:border-neutral-600 transition-all ${
                showFilters ? 'bg-[#2B4C6F] text-white' : 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white'
              }`}
              data-testid="filter-toggle"
            >
              <Funnel size={24} weight="bold" />
              {L('Filters', 'Filtros')}
              {showFilters ? <CaretUp size={20} weight="bold" /> : <CaretDown size={20} weight="bold" />}
            </button>
          </div>

          {/* Quick toggle row */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button
              onClick={() => setFilters(prev => ({ ...prev, verifiedOnly: !prev.verifiedOnly }))}
              className={`flex items-center gap-2 h-9 px-3 text-xs font-black uppercase tracking-wider rounded-full border-2 transition-all ${
                filters.verifiedOnly
                  ? 'bg-emerald-500 text-white border-black shadow-[2px_2px_0px_#000]'
                  : 'bg-white text-neutral-700 border-neutral-300 hover:border-emerald-500'
              }`}
              data-testid="verified-only-pill"
              title={L('Show only license + insurance verified workers', 'Mostrar solo trabajadores con licencia + seguro verificados')}
            >
              <SealCheck size={14} weight="fill" />
              {L('Verified Pro Only', 'Solo Pro Verificados')}
            </button>
            <span className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">
              {L('Verified Pros are automatically boosted in your results.', 'Los Pro Verificados aparecen destacados automáticamente en tus resultados.')}
            </span>
          </div>

          {/* Languages spoken */}
          <div className="mt-4 pt-3 border-t border-neutral-200 dark:border-neutral-700">
            <p className="text-[10px] font-black uppercase tracking-widest text-neutral-500 dark:text-neutral-400 mb-2">
              {L('Languages spoken', 'Idiomas que habla')}
            </p>
            <LanguagesPicker
              value={filters.languages}
              onChange={(next) => setFilters(prev => ({ ...prev, languages: next }))}
              testidPrefix="filter-lang"
            />
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t-2 border-neutral-200 dark:border-neutral-700 grid md:grid-cols-4 gap-4">
              <div>
                <label className="font-bold text-neutral-700 dark:text-neutral-300 block mb-2">{L('Trade', 'Oficio')}</label>
                <select
                  value={filters.trade}
                  onChange={(e) => setFilters(prev => ({ ...prev, trade: e.target.value }))}
                  className="w-full h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl font-bold dark:bg-neutral-700 dark:text-white"
                  data-testid="trade-filter"
                >
                  {tradeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-bold text-neutral-700 dark:text-neutral-300 block mb-2">{L('Min Rating', 'Calificación Mín.')}: {filters.minRating}★</label>
                <Slider
                  value={[filters.minRating]}
                  onValueChange={(val) => setFilters(prev => ({ ...prev, minRating: val[0] }))}
                  max={5}
                  step={0.5}
                  className="mt-4"
                  data-testid="rating-slider"
                />
              </div>
              <div>
                <label className="font-bold text-neutral-700 dark:text-neutral-300 block mb-2">{L('Sort By', 'Ordenar Por')}</label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                  className="w-full h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl font-bold dark:bg-neutral-700 dark:text-white"
                  data-testid="sort-filter"
                >
                  <option value="rating">{L('Highest Rating', 'Mejor Calificación')}</option>
                  <option value="reviews">{L('Most Reviews', 'Más Reseñas')}</option>
                  <option value="experience">{L('Most Experience', 'Más Experiencia')}</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.verifiedOnly}
                    onChange={(e) => setFilters(prev => ({ ...prev, verifiedOnly: e.target.checked }))}
                    className="w-6 h-6 rounded border-2 border-black dark:border-neutral-600"
                    data-testid="verified-filter"
                  />
                  <span className="font-bold text-neutral-700 dark:text-neutral-300 flex items-center gap-2">
                    <ShieldCheck size={20} weight="fill" className="text-green-600" />
                    {L('Verified Only', 'Solo Verificados')}
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="active-filters">
            {activeFilters.map(filter => (
              <span 
                key={filter.key} 
                className="flex items-center gap-2 px-4 py-2 bg-[#2B4C6F] text-white font-bold rounded-full border-2 border-black"
              >
                {filter.label}
                <button onClick={() => clearFilter(filter.key)} className="hover:bg-white/20 rounded-full p-0.5">
                  <X size={16} weight="bold" />
                </button>
              </span>
            ))}
            <button 
              onClick={clearAllFilters}
              className="px-4 py-2 text-neutral-600 dark:text-neutral-400 font-bold hover:text-neutral-900 dark:hover:text-white"
            >
              {L('Clear All', 'Limpiar Todo')}
            </button>
          </div>
        )}

        {/* Workers Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <SpinnerGap size={64} weight="bold" className="animate-spin text-[#2B4C6F]" />
          </div>
        ) : workers.length === 0 ? (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-8 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] text-center">
            <User size={64} weight="light" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-xl font-black text-neutral-900 dark:text-white mb-2">{L('No Workers Found', 'No se Encontraron Trabajadores')}</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-4">{L('Try adjusting your filters', 'Intenta ajustar tus filtros')}</p>
            <button 
              onClick={clearAllFilters}
              className="h-12 px-6 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            >
              {L('Clear Filters', 'Limpiar Filtros')}
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workers.map((worker) => (
              <div 
                key={worker._id}
                className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] hover:shadow-[6px_6px_0px_#000] dark:hover:shadow-[6px_6px_0px_#2B4C6F] hover:translate-y-[-2px] transition-all cursor-pointer"
                onClick={() => navigate(`/worker/${worker._id}`)}
                data-testid={`worker-card-${worker._id}`}
              >
                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-16 h-16 bg-neutral-200 dark:bg-neutral-700 rounded-xl flex items-center justify-center border-2 border-neutral-300 dark:border-neutral-600 overflow-hidden">
                    {worker.avatar ? (
                      <img src={worker.avatar} alt={worker.name} className="w-full h-full object-cover" />
                    ) : (
                      <User size={32} weight="bold" className="text-neutral-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-black text-neutral-900 dark:text-white">{worker.name}</h3>
                      {worker.verified && <VerifiedBadge size="sm" />}
                      {worker.fast_responder && <FastResponderBadge size="sm" />}
                    </div>
                    <p className="text-neutral-600 dark:text-neutral-400 font-medium">{worker.trade || L('Construction Worker', 'Trabajador de Construcción')}</p>
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <AvailabilityBadge workerId={worker._id} size="sm" />
                      {Array.isArray(worker.languages_spoken) && worker.languages_spoken.length > 0 && (
                        <div className="flex items-center gap-1" title={worker.languages_spoken.map(c => (LANGUAGE_CATALOG.find(l => l.code === c)?.label) || c.toUpperCase()).join(', ')}>
                          {worker.languages_spoken.slice(0, 3).map((c) => {
                            const l = LANGUAGE_CATALOG.find(x => x.code === c);
                            if (!l) return null;
                            return (
                              <span key={c} className="text-base leading-none" aria-label={l.label}>{l.flag}</span>
                            );
                          })}
                          {worker.languages_spoken.length > 3 && (
                            <span className="text-[10px] font-bold text-neutral-500">+{worker.languages_spoken.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-2 mb-3">
                  <Star size={24} weight="fill" className="text-yellow-500" />
                  <span className="text-xl font-black text-neutral-900 dark:text-white">{worker.rating?.toFixed(1) || '0.0'}</span>
                  <span className="text-neutral-500 dark:text-neutral-400">({worker.review_count || 0} {L('reviews', 'reseñas')})</span>
                </div>

                {/* Location */}
                <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 mb-3">
                  <MapPin size={20} weight="bold" />
                  <span className="font-medium">{worker.location || L('Location not specified', 'Ubicación no especificada')}</span>
                </div>

                {/* Skills */}
                {worker.skills && worker.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {worker.skills.slice(0, 3).map((skill, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-[#2B4C6F]/10 dark:bg-[#2B4C6F]/30 border-2 border-[#2B4C6F]/30 rounded-full text-sm font-bold text-[#2B4C6F] dark:text-white"
                      >
                        {skill}
                      </span>
                    ))}
                    {worker.skills.length > 3 && (
                      <span className="px-3 py-1 bg-neutral-100 dark:bg-neutral-700 border-2 border-neutral-300 dark:border-neutral-600 rounded-full text-sm font-bold text-neutral-500 dark:text-neutral-400">
                        +{worker.skills.length - 3}
                      </span>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/worker/${worker._id}`);
                    }}
                    className="flex-1 h-12 flex items-center justify-center gap-2 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                  >
                    <Briefcase size={20} weight="bold" />
                    {L('View Profile', 'Ver Perfil')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/messages?user=${worker._id}`);
                    }}
                    className="h-12 w-12 flex items-center justify-center bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 rounded-xl border-2 border-neutral-300 dark:border-neutral-600 hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-all"
                  >
                    <ChatCircle size={20} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > 12 && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="h-12 px-6 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 font-bold rounded-xl border-2 border-black dark:border-neutral-700 shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F] disabled:opacity-50 disabled:shadow-none active:translate-y-1 active:shadow-none transition-all"
            >
              {L('Previous', 'Anterior')}
            </button>
            <span className="text-neutral-700 dark:text-neutral-300 font-bold">
              {L('Page', 'Página')} {page} {L('of', 'de')} {Math.ceil(total / 12)}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= Math.ceil(total / 12)}
              className="h-12 px-6 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 font-bold rounded-xl border-2 border-black dark:border-neutral-700 shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F] disabled:opacity-50 disabled:shadow-none active:translate-y-1 active:shadow-none transition-all"
            >
              {L('Next', 'Siguiente')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FindWorkers;
